'use client'
// Technology catalogue grid: image-forward (Veronika's "showroom, not an index", 2026-07-17) with
// the handbook rules: no card boxes, no badges, no gradient placeholders; workflow filter as the
// same tab style as the regulation regions. "All" shows one section per workflow, its categories
// in process order (catalogue study, 2026-10-12): photos on one tile treatment, the categories
// without a photo as text items after them, the suppliers named.
import React, { useState } from 'react'

export type CatalogueCard = {
  slug: string
  name: string
  excerpt: string
  href: string
  image: string | null
  /** cutouts on white are contained on the tile; scene photos fill it */
  fit?: 'contain' | 'cover'
  supplierCount: number
  workflow: string
  suppliers: { name: string; href: string }[]
  stage: { n: number; title: string; href: string } | null
  order: number
}

function Meta({ c }: { c: CatalogueCard }) {
  return (
    <div className="hb-cat-meta">
      {c.suppliers.length > 0 ? (
        <p className="hb-cat-sup">
          {c.suppliers.slice(0, 2).map((s, i) => (
            <React.Fragment key={s.name}>{i > 0 ? ', ' : ''}<a href={s.href}>{s.name}</a></React.Fragment>
          ))}
          {c.suppliers.length > 2 ? ` and ${c.suppliers.length - 2} more` : ''}
        </p>
      ) : (
        <p>Suppliers for this category are under review.</p>
      )}
      {c.stage ? <p>Stage {c.stage.n} · <a href={c.stage.href}>{c.stage.title}</a></p> : null}
    </div>
  )
}

export function CatalogueGrid({ cards, workflows }: { cards: CatalogueCard[]; workflows: string[] }) {
  const [active, setActive] = useState('All')
  const tabs = ['All', ...workflows.filter((t) => cards.some((c) => c.workflow === t))]
  const sections = (active === 'All' ? tabs.slice(1) : [active]).map((t) => ({
    t,
    items: cards.filter((c) => c.workflow === t).sort((a, b) => a.order - b.order),
  }))
  const headed = sections.length > 1
  return (
    <>
      {tabs.length > 2 && (
        <div className="hb-tabs" role="tablist" aria-label="Filter by workflow">
          {tabs.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={active === t} className="hb-tab" onClick={() => setActive(t)}>
              {t} <span className="hb-tab-n">{t === 'All' ? cards.length : cards.filter((c) => c.workflow === t).length}</span>
            </button>
          ))}
        </div>
      )}
      {sections.map(({ t, items }) => {
        const photos = items.filter((c) => c.image)
        const text = items.filter((c) => !c.image)
        return (
          <section key={t} className="hb-sec hb-cat-sec">
            {headed ? <h2>{t}</h2> : null}
            <ul className="hb-cat">
              {photos.map((c) => (
                <li key={c.slug} className="hb-cat-item">
                  <a href={c.href} className={`hb-cat-img hb-cat-${c.fit || 'cover'}`} tabIndex={-1} aria-hidden="true">
                    <img src={c.image!} alt={c.name} loading="lazy" />
                  </a>
                  <a href={c.href} className="hb-cat-name">{c.name}</a>
                  {c.excerpt ? <p className="hb-cat-desc">{c.excerpt}</p> : <span />}
                  <Meta c={c} />
                </li>
              ))}
              {/* no photo yet: the same column, text from the top under a rule */}
              {text.map((c) => (
                <li key={c.slug} className="hb-cat-titem">
                  <a href={c.href} className="hb-cat-name">{c.name}</a>
                  {c.excerpt ? <p className="hb-cat-desc">{c.excerpt}</p> : null}
                  <Meta c={c} />
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </>
  )
}
